import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowDownRight, ArrowUpRight, Repeat, ChevronDown } from "lucide-react";

const flow = [
  {
    icon: ArrowDownRight,
    label: "Capital In",
    visible: "Build capital inside a system designed for protection, liquidity, and future use.",
    expanded: "Capital has to be created before it can be accessed. A properly structured policy is designed around the family’s goals, cash flow, and need for early and long-term value. The goal is not simply to own life insurance. It is to create a useful financial asset with clearly understood contractual terms.",
  },
  {
    icon: Repeat,
    label: "Borrow and Repay",
    visible: "Access available capital for appropriate needs, then intentionally rebuild the system.",
    expanded: "Policy loans may provide access to capital without requiring the policy owner to liquidate the policy. Loans are not free money. Interest, repayment behavior, policy performance, and contract terms matter. Repayment helps restore liquidity and reinforces the discipline of treating the family system as a real financial asset.",
  },
  {
    icon: ArrowUpRight,
    label: "Compound On",
    visible: "Allow the system to continue developing while capital is being used elsewhere.",
    expanded: "Depending on the contract and policy performance, policy values may continue developing while a policy loan is outstanding. This does not eliminate loan costs or risk. The benefit is coordination: the family learns how capital accumulation, access, repayment, protection, and long-term growth can function within one system.",
  },
];

export const InfiniteBankingIntro = () => {
  const [open, setOpen] = useState(-1);
  return (
    <section id="infinite-banking" data-testid="infinite-banking-section" className="section-lr relative bg-legacy text-ivory">
      <div className="container-lr relative grid gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
        <div>
          <div className="flex items-center gap-4"><span className="gold-rule" /><p className="eyebrow text-gold">Family Banking System</p></div>
          <h2 className="section-heading mt-6 text-ivory">Become your own source of <span className="accent-word">financing.</span></h2>
          <p className="mt-6 text-base leading-relaxed text-taupe md:text-lg">Infinite Banking is a process, not a product. The goal is to create a disciplined way to store, access, repay, and reuse capital with greater intention.</p>
          <p className="mt-6 text-sm font-semibold tracking-wide text-gold">Click each arrow to learn more.</p>
          <div className="mt-9 flex flex-col gap-4 sm:flex-row">
            <Link to="/infinite-banking" data-testid="explore-ib" className="btn-base btn-gold">Explore Infinite Banking <ArrowUpRight size={18} /></Link>
          </div>
        </div>
        <div className="space-y-4">
          {flow.map(({ icon: Icon, label, visible, expanded }, index) => {
            const isOpen = open === index;
            const contentId = `banking-step-${index}`;
            return (
              <motion.article key={label} initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.08 }} className="overflow-hidden rounded-card border border-ivory/12 bg-white/[0.04]">
                <button type="button" aria-expanded={isOpen} aria-controls={contentId} onClick={() => setOpen(isOpen ? -1 : index)} className="focus-ring flex w-full items-start gap-5 p-6 text-left md:p-7">
                  <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-gold/40 text-gold"><Icon size={22} strokeWidth={1.5} /></span>
                  <span className="flex-1"><span className="flex items-center justify-between gap-3"><span className="font-serif text-xl text-ivory">{label}</span><ChevronDown size={18} className={`text-gold transition-transform ${isOpen ? "rotate-180" : ""}`} /></span><span className="mt-2 block text-sm leading-relaxed text-taupe">{visible}</span></span>
                </button>
                <div id={contentId} className={`accordion-panel ${isOpen ? "is-open" : ""}`}>
                  <div>
                    <p className="px-6 pb-6 pl-[5.75rem] text-sm leading-relaxed text-taupe md:px-7 md:pb-7 md:pl-[6.25rem]">{expanded}</p>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
      <div className="container-lr relative mt-16 md:mt-20">
        <div className="mx-auto flex max-w-sm items-center gap-4">
          <span className="h-px flex-1 bg-gradient-to-r from-transparent to-gold/60" />
          <span className="h-2.5 w-2.5 rotate-45 bg-gold" />
          <span className="h-px flex-1 bg-gradient-to-l from-transparent to-gold/60" />
        </div>
      </div>
    </section>
  );
};
